import JSZip from 'jszip';
import type { SplitChunk, SplitMode, WorkerMessage } from './types.ts';
import { createWorker } from './worker-loader.ts';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

const dropZone = $<HTMLLabelElement>('drop-zone');
const fileInput = $<HTMLInputElement>('file-input');
const fileMeta = $<HTMLSpanElement>('file-meta');
const configureCard = $<HTMLElement>('configure-card');
const separatorSelect = $<HTMLSelectElement>('separator');
const separatorCustom = $<HTMLInputElement>('separator-custom');
const colCountLabel = $<HTMLSpanElement>('col-count');
const rowsField = $<HTMLDivElement>('rows-field');
const sizeField = $<HTMLDivElement>('size-field');
const rowsPerSplit = $<HTMLInputElement>('rows-per-split');
const mbPerSplit = $<HTMLInputElement>('mb-per-split');
const includeHeader = $<HTMLInputElement>('include-header');
const splitBtn = $<HTMLButtonElement>('split-btn');
const progressBar = $<HTMLDivElement>('progress-bar');
const progressFill = $<HTMLDivElement>('progress-fill');
const progressText = $<HTMLParagraphElement>('progress-text');
const resultsList = $<HTMLUListElement>('results-list');
const resultsFoot = $<HTMLDivElement>('results-foot');
const resultsSummary = $<HTMLSpanElement>('results-summary');
const downloadZipBtn = $<HTMLButtonElement>('download-zip');

const MAX_FILE_BYTES = 100 * 1024 * 1024;

let currentFile: File | null = null;
let currentChunks: SplitChunk[] = [];

// ---------- file selection ----------

dropZone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropZone.classList.add('drag-over');
});
dropZone.addEventListener('dragleave', () => dropZone.classList.remove('drag-over'));
dropZone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropZone.classList.remove('drag-over');
  const f = e.dataTransfer?.files?.[0];
  if (f) void onFile(f);
});
fileInput.addEventListener('change', () => {
  const f = fileInput.files?.[0];
  if (f) void onFile(f);
});

async function onFile(file: File) {
  if (file.size > MAX_FILE_BYTES) {
    currentFile = null;
    fileMeta.textContent = `${file.name} · ${formatBytes(file.size)} — too large. Limit is ${formatBytes(MAX_FILE_BYTES)}.`;
    configureCard.classList.add('hidden');
    return;
  }
  currentFile = file;
  fileMeta.textContent = `${file.name} · ${formatBytes(file.size)}`;
  configureCard.classList.remove('hidden');
  resetOutputPanel();
  await refreshSeparatorPreview();
}

// ---------- separator preview ----------

separatorSelect.addEventListener('change', async () => {
  separatorCustom.classList.toggle('hidden', separatorSelect.value !== 'custom');
  await refreshSeparatorPreview();
});
separatorCustom.addEventListener('input', () => void refreshSeparatorPreview());

async function refreshSeparatorPreview() {
  if (!currentFile) return;
  const sample = await readSample(currentFile, 16 * 1024);
  const sep = resolveSeparator(sample);
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? '';
  const cols = countUnquoted(firstLine, sep);
  colCountLabel.textContent = `${cols}  (separator: ${displaySep(sep)})`;
}

function resolveSeparator(sample: string): string {
  const v = separatorSelect.value;
  if (v === 'auto') return detectSeparator(sample);
  if (v === 'custom') return separatorCustom.value || ',';
  if (v === '\\t') return '\t';
  return v;
}

function detectSeparator(sample: string): string {
  const candidates = [',', ';', '\t', '|'];
  const lines = sample.split(/\r?\n/).slice(0, 5).filter((l) => l.length > 0);
  let best = ',';
  let bestScore = -1;
  for (const c of candidates) {
    const counts = lines.map((l) => countUnquoted(l, c));
    const first = counts[0] ?? 0;
    // Require the separator to actually appear at least once on the
    // header line (countUnquoted always returns >=1 for "single column").
    if (first < 2) continue;
    const consistent = counts.every((n) => n === first);
    const score = consistent ? first * 10 : first;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  return best;
}

function countUnquoted(line: string, sep: string): number {
  let count = 1;
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuote && line[i + 1] === '"') { i++; continue; }
      inQuote = !inQuote;
    } else if (!inQuote && line.startsWith(sep, i)) {
      count++;
      i += sep.length - 1;
    }
  }
  return count;
}

function displaySep(sep: string): string {
  if (sep === '\t') return 'TAB';
  if (sep === ' ') return 'SPACE';
  return sep;
}

async function readSample(file: File, bytes: number): Promise<string> {
  const slice = file.slice(0, Math.min(bytes, file.size));
  return await slice.text();
}

// ---------- mode toggle ----------

document.querySelectorAll<HTMLInputElement>('input[name="mode"]').forEach((r) => {
  r.addEventListener('change', () => {
    const mode = r.value as SplitMode;
    rowsField.classList.toggle('hidden', mode !== 'rows');
    sizeField.classList.toggle('hidden', mode !== 'size');
  });
});

// ---------- split trigger ----------

splitBtn.addEventListener('click', () => {
  if (!currentFile) return;
  const mode = (document.querySelector<HTMLInputElement>('input[name="mode"]:checked')?.value ?? 'rows') as SplitMode;
  const rows = Math.max(1, parseInt(rowsPerSplit.value, 10) || 1);
  const mb = Math.max(0.1, parseFloat(mbPerSplit.value) || 0.1);
  void startSplit(currentFile, mode, rows, mb);
});

async function startSplit(file: File, mode: SplitMode, rows: number, mb: number) {
  const sample = await readSample(file, 16 * 1024);
  const separator = resolveSeparator(sample);

  currentChunks = [];
  resultsList.replaceChildren();
  resultsFoot.classList.add('hidden');
  downloadZipBtn.classList.add('hidden');
  progressBar.classList.remove('hidden');
  progressFill.style.width = '0%';
  progressText.textContent = 'Starting…';
  splitBtn.disabled = true;

  const worker = createWorker();

  worker.onmessage = (ev: MessageEvent<WorkerMessage>) => {
    const msg = ev.data;
    if (msg.type === 'progress') {
      const pct = msg.totalBytes ? (msg.processedBytes / msg.totalBytes) * 100 : 0;
      progressFill.style.width = `${Math.min(99, pct).toFixed(1)}%`;
      progressText.textContent = `Processed ${formatBytes(msg.processedBytes)} / ${formatBytes(msg.totalBytes)} · ${msg.splits} splits so far`;
    } else if (msg.type === 'chunk') {
      currentChunks.push(msg.chunk);
      appendResultRow(msg.chunk);
    } else if (msg.type === 'done') {
      progressFill.style.width = '100%';
      progressText.textContent = `Done. ${msg.totalSplits} splits · ${msg.totalRows.toLocaleString()} rows total.`;
      resultsSummary.textContent = `${msg.totalSplits} files · ${formatBytes(currentChunks.reduce((a, c) => a + c.bytes, 0))} total`;
      resultsFoot.classList.remove('hidden');
      if (currentChunks.length > 1) downloadZipBtn.classList.remove('hidden');
      splitBtn.disabled = false;
      worker.terminate();
    } else if (msg.type === 'error') {
      progressText.textContent = `Error: ${msg.message}`;
      splitBtn.disabled = false;
      worker.terminate();
    }
  };

  worker.postMessage({
    file,
    separator,
    mode,
    rowsPerSplit: rows,
    bytesPerSplit: Math.round(mb * 1024 * 1024),
    includeHeader: includeHeader.checked
  });
}

function appendResultRow(chunk: SplitChunk) {
  const li = document.createElement('li');
  const url = URL.createObjectURL(chunk.blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = chunk.name;
  a.textContent = chunk.name;
  const meta = document.createElement('span');
  meta.className = 'meta';
  meta.textContent = `${chunk.rows.toLocaleString()} rows · ${formatBytes(chunk.bytes)}`;
  li.appendChild(a);
  li.appendChild(meta);
  resultsList.appendChild(li);
}

// ---------- ZIP download ----------

downloadZipBtn.addEventListener('click', async () => {
  if (currentChunks.length === 0) return;
  downloadZipBtn.disabled = true;
  const zip = new JSZip();
  for (const c of currentChunks) zip.file(c.name, c.blob);
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 }
  });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${stripExt(currentFile?.name ?? 'splits')}_splits.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  downloadZipBtn.disabled = false;
});

// ---------- utils ----------

function resetOutputPanel() {
  resultsList.replaceChildren();
  resultsFoot.classList.add('hidden');
  downloadZipBtn.classList.add('hidden');
  progressBar.classList.add('hidden');
  progressFill.style.width = '0%';
  progressText.textContent = 'Configure split options and click Split CSV to begin.';
  currentChunks = [];
}

function stripExt(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}
